<!-- Physical Strength Results -->
<script setup lang="ts">
import { useStore } from '@/stores/main';
import { computed, inject, ref, onMounted, watch, onUnmounted } from 'vue';

// Accept page data as a prop from parent component
const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  }
});

const categoryId = 64; // Physical strength category ID
const store = useStore();

const questionsAnswered = computed(() => {
  // Safely check if profile values exist
  const profileValues = store.profile?.values;
  if (!profileValues) return false;
  
  return (
    store.getProfileValue('strLegs2') !== null &&
    store.getProfileValue('pushups') !== null &&
    store.getProfileValue('strAb') !== null
  );
});

const factor = computed(() => {
  if (!store.profile?.factors) return null;
  return store.profile.factors[categoryId];
});

async function handleImprove(value: string) {
  try {
    await store.setImproveValue(categoryId, value);
  } catch (error) {
    console.error('Error setting improve value:', error);
  }
}

const colorScheme = inject('colorScheme', 'green');
const borderColor = computed(() => {
  return (
    {
      purple: 'border-purple-light',
      yellow: 'border-yellow-light',
      green: 'border-green-light',
      blue: 'border-blue-light',
    }[colorScheme] ?? 'border-green-light'
  );
});

const improve = computed(() => {
  return factor.value?.improve || null;
});

// Rating scale functionality
const sectionResults = ref([]);
const currentValue = ref('');
const debugCurrentPage = ref('');
const debugFactorInfo = ref([]);
const strengthValues = ref({
  ben: 0,
  armar: 0,
  mage: 0
});
let intervalId;

// Calculate average of the three strength values
const calculateStrengthAverage = () => {
  try {
    const sum = strengthValues.value.ben + strengthValues.value.armar + strengthValues.value.mage;
    if (sum === 0) return 0;
    
    const average = Math.round(sum / 3);
    return average;
  } catch (error) {
    console.error('Error calculating strength average:', error);
    return 0;
  }
};

// Computed average strength value
const averageStrength = computed(() => {
  return calculateStrengthAverage();
});

const fetchSectionResults = () => {
  try {
    const storedResults = localStorage.getItem('sectionResults');
    if (!storedResults) return;
    
    const parsedResults = JSON.parse(storedResults);
    if (!Array.isArray(parsedResults)) {
      console.warn('sectionResults is not an array');
      return;
    }
    
    sectionResults.value = parsedResults;
    
    // Create debug information
    if (sectionResults.value.length > 0) {
      // Update debug info
      debugFactorInfo.value = sectionResults.value.map(section => {
        if (!section) return { pageName: 'unknown', factors: [] };
        
        return {
          pageName: section.pageName || 'unknown',
          factors: Array.isArray(section.factors) 
            ? section.factors.map(f => ({
                name: f?.name || 'unknown',
                value: f?.value || 0
              })) 
            : []
        };
      });
      
      // Find physical strength values
      sectionResults.value.forEach((section) => {
        if (!section || section.pageName !== 'physical' || !Array.isArray(section.factors)) {
          return;
        }
        
        // Find the specific strength values
        section.factors.forEach((f) => {
          if (!f || !f.name) return;
          
          if (f.name === 'Styrka Ben') {
            strengthValues.value.ben = parseInt(f.value) || 0;
          } else if (f.name === 'Styrka Armar') {
            strengthValues.value.armar = parseInt(f.value) || 0;
          } else if (f.name === 'Styrka Mage') {
            strengthValues.value.mage = parseInt(f.value) || 0;
          }
        });
        
        // Update the current value with the average
        const avg = calculateStrengthAverage();
        if (avg > 0) {
          currentValue.value = String(avg);
        }
      });
    }
  } catch (error) {
    console.error('Error fetching section results:', error);
  }
};

onMounted(() => {
  fetchSectionResults();
  
  // Set up interval to refresh the data
  intervalId = window.setInterval(fetchSectionResults, 2000);

  // Initialize currentValue from factor if available
  if (factor.value?.value) {
    currentValue.value = String(factor.value.value);
  }
});

onUnmounted(() => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
});

const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];

const highlightSwatch = (index: number) => {
  if (currentValue.value === String(index) || averageStrength.value === index) {
    return 'scale-150';
  }
  return '';
};

// Toggle for showing debug info
const showDebug = ref(false);
</script>

<template>
  <div class="px-8 pt-6 gap-y-4 w-full lg:w-[60%]">
    <h3 class="m-0 mb-2 font-semibold text-lg text-gray-800">Sammanfattning: Styrka</h3>

    <div v-if="!questionsAnswered" class="mt-4 text-base text-gray-500 italic">
      Du har inte svarat på alla styrkefrågor ännu.
    </div>

    <template v-else-if="questionsAnswered && factor">
      <!-- Status text -->
      <p class="text-base text-gray-700 mt-1 mb-4">
        Du har <strong>{{ factor?.label === 'okänd' ? 'okänt' : (factor?.label || '') }}</strong> värde.
      </p>

      <!-- Strength values card -->
      <div v-if="averageStrength > 0" class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
        <h4 class="text-sm font-semibold text-gray-600 uppercase tracking-wide m-0 mb-4">Dina styrkevärden</h4>
        <div class="grid grid-cols-3 gap-3 text-center mb-4">
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-2xl font-bold text-gray-800">{{ strengthValues.ben }}</div>
            <div class="text-xs text-gray-500 mt-1">Ben</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-2xl font-bold text-gray-800">{{ strengthValues.armar }}</div>
            <div class="text-xs text-gray-500 mt-1">Armar</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-2xl font-bold text-gray-800">{{ strengthValues.mage }}</div>
            <div class="text-xs text-gray-500 mt-1">Mage</div>
          </div>
        </div>
        <div class="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
          <span class="text-sm text-gray-500">Genomsnitt:</span>
          <span class="text-xl font-bold" :class="averageStrength >= 3 ? 'text-green-600' : 'text-orange-500'">{{ averageStrength }}</span>
        </div>
      </div>

      <!-- Rating Scale -->
      <div class="w-full flex justify-center my-6">
        <div class="flex justify-center space-x-2 items-center text-base w-full max-w-md">
          <div class="flex-1 flex text-sm items-center">
            <div class="flex-1 border-0 border-t border-solid border-black/30"></div>
            <div class="px-1">Risk</div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[1],
                  highlightSwatch(1),
                ]"
                >1</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[2],
                  highlightSwatch(2),
                ]"
                >2</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[3],
                  highlightSwatch(3),
                ]"
                >3</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[4],
                  highlightSwatch(4),
                ]"
                >4</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[5],
                  highlightSwatch(5),
                ]"
                >5</span
              >
            </div>
          </div>
          <div class="flex-1 flex text-sm items-center">
            <div class="px-1">Frisk</div>
            <div class="flex-1 border-0 border-t border-solid border-black/30"></div>
          </div>
        </div>
      </div>

      <!-- Improve radio buttons -->
      <ul class="flex flex-col m-0 mt-4 p-0">
        <li class="list-none">
          <b-radio
            :name="`physicalStrength-improve`"
            :color="colorScheme"
            value="1"
            label="Detta är jag nöjd med"
            :label-id="`physicalStrength-improve-1-label`"
            @change="handleImprove('1')"
            v-model="improve"
          />
        </li>
        <li class="list-none">
          <b-radio
            :name="`physicalStrength-improve`"
            :color="colorScheme"
            value="2"
            label="Detta vill jag förbättra"
            :label-id="`physicalStrength-improve-2-label`"
            @change="handleImprove('2')"
            v-model="improve"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped>
/* Transitions for smoother scale effect */
.scale-150 {
  transform: scale(1.5);
  transition: transform 0.3s ease;
}

span {
  transition: transform 0.3s ease, background-color 0.3s ease;
}
</style>